import { EditorComponent } from '@tinymce/tinymce-angular';

type TinyMceEditor = NonNullable<EditorComponent['editor']>;
const DOCUMENT_HEADER_SPACER_SELECTOR = '[data-document-spacer="header"]';

export function moveBlockToDocumentEnd(element: Element): void {
  const body = element.ownerDocument.body;

  if (!body || !body.contains(element)) {
    return;
  }

  let topLevelBlock = element;

  while (topLevelBlock.parentElement && topLevelBlock.parentElement !== body) {
    topLevelBlock = topLevelBlock.parentElement;
  }

  if (topLevelBlock.parentElement === body && topLevelBlock !== body.lastElementChild) {
    body.appendChild(topLevelBlock);
  }
}

export function escapeHtmlAttribute(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

export function createDocumentHeaderHtml(emblemSrc: string, variant: 1 | 2 = 1): string {
  const safeEmblemSrc = escapeHtmlAttribute(emblemSrc);
  const cellStyle = [
    'border:1px solid #cbd5e1',
    'padding:8px 10px',
    'text-align:center',
    'vertical-align:middle',
    'font-size:10pt',
    'font-weight:700',
    'line-height:1.25',
  ].join(';');

  const kgText1 = 'КЫРГЫЗ РЕСПУБЛИКАСЫНЫН<br>МИНИСТРЛЕР КАБИНЕТИНЕ<br>КАРАШТУУ МАМЛЕКЕТТИК САЛЫК<br>КЫЗМАТЫ';
  const kgText2 = 'КЫРГЫЗ РЕСПУБЛИКАСЫНЫН<br>ФИНАНСЫ МИНИСТРЛИГИНЕ<br>КАРАШТУУ МАМЛЕКЕТТИК<br>САЛЫК КЫЗМАТЫ';
  const ruText1 = 'ГОСУДАРСТВЕННАЯ<br>НАЛОГОВАЯ СЛУЖБА<br>ПРИ КАБИНЕТЕ МИНИСТРОВ<br>КЫРГЫЗСКОЙ РЕСПУБЛИКИ';
  const ruText2 = 'ГОСУДАРСТВЕННАЯ<br>НАЛОГОВАЯ СЛУЖБА<br>ПРИ МИНИСТЕРСТВЕ ФИНАНСОВ<br>КЫРГЫЗСКОЙ РЕСПУБЛИКИ';

  return `
    <table
      data-document-block="header"
      class="document-header-table"
      aria-label="Шапка документа"
      style="width:100%;max-width:100%;margin:0 auto 28px;border-collapse:collapse;table-layout:fixed"
    >
      <colgroup>
        <col style="width:36%">
        <col style="width:28%">
        <col style="width:36%">
      </colgroup>
      <tbody>
        <tr style="height:104px">
          <td style="${cellStyle}">
          ${variant === 1 ? kgText1 : kgText2}
          </td>
          <td style="${cellStyle}">
            <img
              src="${safeEmblemSrc}"
              alt="Кыргыз Республикасынын герби"
              width="104"
              style="display:block;width:104px;max-width:100%;height:auto;margin:0 auto"
            >
          </td>
          <td style="${cellStyle}">
             ${variant === 1 ? ruText1 : ruText2}
          </td>
        </tr>
        <tr style="height:54px">
          <td style="${cellStyle}">&nbsp;</td>
          <td style="${cellStyle}">БУЙРУК<br>ПРИКАЗ</td>
          <td style="${cellStyle}">&nbsp;</td>
        </tr>
        <tr style="height:34px">
          <td style="${cellStyle};font-weight:400;white-space:nowrap">______________ 20___-ж. / г. № __________</td>
          <td style="${cellStyle};font-weight:400;white-space:nowrap"></td>
          <td style="${cellStyle};font-weight:400;text-align:right;white-space:nowrap">г. Бишкек ш.</td>
        </tr>
      </tbody>
    </table>
  `;
}

export function createDocumentSignatureHtml(): string {
  return `
    <table
      data-document-block="signature"
      class="document-signature-table"
      aria-label="Место для подписи"
      style="width:100%;max-width:100%;margin:7.5rem auto 0;border:0;border-collapse:collapse"
    >
      <tbody>
        <tr>
          <td style="width:50%;border:0;padding:0;text-align:left;vertical-align:bottom">
            <strong>Должность</strong>
          </td>
          <td style="width:50%;border:0;padding:0;text-align:right;vertical-align:bottom">
            <strong>Фамилия</strong>
          </td>
        </tr>
      </tbody>
    </table>
  `;
}

export function insertSingletonBlock(
  editor: TinyMceEditor,
  selector: string,
  html: string,
  position: 'start' | 'end',
): void {
  const body = editor.getBody();

  editor.undoManager.transact(() => {
    const existingBlocks = Array.from(body.querySelectorAll<HTMLElement>(selector));
    const blockHtml = existingBlocks.at(0)?.outerHTML ?? html;

    existingBlocks.forEach((block) => block.remove());

    if (position === 'start') {
      body.querySelectorAll(DOCUMENT_HEADER_SPACER_SELECTOR).forEach((node) => node.remove());
      editor.selection.setCursorLocation(body, 0);
      editor.insertContent(
        `${blockHtml}<p data-document-spacer="header" style="margin:.4em 0"><br data-mce-bogus="1"></p>`,
      );

      return;
    }

    editor.selection.setCursorLocation(body, body.childNodes.length);
    editor.insertContent(blockHtml);
  });

  editor.focus();
  editor.nodeChanged();
}

export function normalizeTables(root: Element): void {
  const tables = root.matches('table')
    ? [root as HTMLTableElement]
    : Array.from(root.querySelectorAll<HTMLTableElement>('table'));

  for (const table of tables) {
    table.removeAttribute('width');
    table.removeAttribute('align');
    table.classList.add('editor-table');
    table.style.removeProperty('margin');
    table.style.setProperty('width', '100%', 'important');
    table.style.setProperty('max-width', '100%', 'important');
    table.style.setProperty('margin-left', 'auto', 'important');
    table.style.setProperty('margin-right', 'auto', 'important');
    table.style.setProperty('border-collapse', 'collapse');

    for (const cell of table.querySelectorAll<HTMLTableCellElement>('td, th')) {
      cell.removeAttribute('width');
      cell.style.removeProperty('min-width');
      cell.style.removeProperty('max-width');
    }
  }
}

export function isUsableImageSource(src: string | null): boolean {
  if (!src) {
    return false;
  }

  return !/^(?:file|mhtml|wordml):/i.test(src) && src !== 'about:blank';
}

export function getImageFileFromDataTransfer(clipboardData: DataTransfer | null): File | null {
  if (!clipboardData) {
    return null;
  }

  const imageItem = Array.from(clipboardData.items).find(
    (item) => item.kind === 'file' && item.type.toLowerCase().startsWith('image/'),
  );

  return imageItem?.getAsFile() ?? null;
}

export function createImagePasteHtml(
  document: Document,
  html: string,
  plainText: string,
  imageSrc: string,
): string {
  const root = document.createElement('div');

  if (html) {
    root.innerHTML = html;
  } else if (plainText) {
    const paragraph = document.createElement('p');
    paragraph.textContent = plainText;
    root.appendChild(paragraph);
  }

  const images = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
  const invalidImages = images.filter((image) => !isUsableImageSource(image.getAttribute('src')));

  if (invalidImages.length) {
    invalidImages.forEach((image) => image.setAttribute('src', imageSrc));
  } else if (!images.length) {
    const image = document.createElement('img');
    image.src = imageSrc;
    image.alt = '';
    image.style.maxWidth = '100%';
    image.style.height = 'auto';
    root.appendChild(image);
  }

  return root.innerHTML;
}

export function restorePastedHeaderEmblem(root: Element, emblemSrc: string): void {
  const pastedText = (root.textContent ?? '').replace(/\s+/g, ' ').toUpperCase();
  const looksLikeDocumentHeader =
    pastedText.includes('КЫРГЫЗ РЕСПУБЛИКАСЫНЫН') && pastedText.includes('ГОСУДАРСТВЕННАЯ НАЛОГОВАЯ СЛУЖБА');

  if (!looksLikeDocumentHeader) {
    return;
  }

  const table = root.matches('table') ? (root as HTMLTableElement) : root.querySelector<HTMLTableElement>('table');

  if (!table) {
    return;
  }

  table.dataset['documentBlock'] = 'header';
  table.classList.add('document-header-table');

  const currentImage = table.querySelector<HTMLImageElement>('img');

  if (currentImage && isUsableImageSource(currentImage.getAttribute('src'))) {
    currentImage.alt ||= 'Кыргыз Республикасынын герби';
    currentImage.style.setProperty('display', 'block');
    currentImage.style.setProperty('max-width', '104px');
    currentImage.style.setProperty('height', 'auto');
    currentImage.style.setProperty('margin', '0 auto');

    return;
  }

  const firstRow = table.rows.item(0);
  const centerCell = firstRow ? firstRow.cells.item(Math.floor(firstRow.cells.length / 2)) : null;

  if (!centerCell) {
    return;
  }

  currentImage?.remove();

  const image = table.ownerDocument.createElement('img');

  image.src = emblemSrc;
  image.alt = 'Кыргыз Республикасынын герби';
  image.width = 104;
  image.style.display = 'block';
  image.style.width = '104px';
  image.style.maxWidth = '100%';
  image.style.height = 'auto';
  image.style.margin = '0 auto';
  centerCell.insertBefore(image, centerCell.firstChild);
}
